import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmt, STATUS_META, TIER_RANK, rankByTier } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { LineChart, Line, ResponsiveContainer } from "recharts";
import { MagnifyingGlassIcon, ArrowUpIcon, ArrowDownIcon, WarningIcon } from "@phosphor-icons/react";
import BotName from "@/components/BotName";

const STATUSES = ["all", "champion", "elite", "basic", "testing", "graveyard"];

function SuspiciousBadge() {
  return (
    <span
      data-testid="suspicious-badge"
      title="Unverified DD - profit + zero drawdown flagged as data quality issue"
      className="inline-flex items-center gap-1 text-[9px] mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-[#FF4D4D]/40 bg-[#FF4D4D]/10 text-[#FF4D4D] ml-1.5"
    >
      <WarningIcon size={9} weight="fill" /> unverified
    </span>
  );
}

function SortHeader({ k, children, align = "left", sortKey, sortDir, onToggle }) {
  return (
    <th
      data-testid={`sort-${k}`}
      onClick={() => onToggle(k)}
      className={`text-${align} py-2 font-normal cursor-pointer hover:text-[#D6FF38] transition-colors select-none`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortKey === k && (sortDir === "asc" ? <ArrowUpIcon size={10} /> : <ArrowDownIcon size={10} />)}
      </span>
    </th>
  );
}

export default function Leaderboard() {
  const [bots, setBots] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [symbolFilter, setSymbolFilter] = useState("all");
  // Default sort = tier (Champion > Elite > Basic > Testing) with growth_pct
  // as tiebreaker. This ensures the #1 spot always belongs to a Champion,
  // never overtaken by an Elite/Basic bot with a fluke high growth number.
  const [sortKey, setSortKey] = useState("tier");
  const [sortDir, setSortDir] = useState("desc");
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/bots").then((r) => setBots(r.data.bots)).catch(console.error);
  }, []);

  const symbols = useMemo(() => ["all", ...Array.from(new Set(bots.map((b) => b.symbol))).sort()], [bots]);

  const filtered = useMemo(() => {
    let arr = bots;
    if (statusFilter !== "all") arr = arr.filter((b) => b.status === statusFilter);
    if (symbolFilter !== "all") arr = arr.filter((b) => b.symbol === symbolFilter);
    if (search) arr = arr.filter((b) => b.name.toLowerCase().includes(search.toLowerCase()));
    arr = [...arr].sort((a, b) => {
      // Special virtual key: tier = rank by tier priority + growth_pct tiebreak
      if (sortKey === "tier") {
        const cmp = rankByTier(a, b);
        return sortDir === "asc" ? -cmp : cmp;
      }
      const av = a[sortKey], bv = b[sortKey];
      if (typeof av === "string") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      const primary = sortDir === "asc" ? av - bv : bv - av;
      // Tie-breaker on ALL sorts: fall back to tier priority so ties never
      // put an Elite above a Champion accidentally.
      if (primary === 0) return (TIER_RANK[b?.status] ?? 0) - (TIER_RANK[a?.status] ?? 0);
      return primary;
    });
    return arr;
  }, [bots, statusFilter, symbolFilter, search, sortKey, sortDir]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("desc"); }
  };

  const sortProps = { sortKey, sortDir, onToggle: toggleSort };

  return (
    <div data-testid="leaderboard-page" className="p-4 md:p-6 space-y-4">
      <div>
        <div className="text-[10px] mono uppercase tracking-widest text-[#D6FF38]/70">Compound Leaderboard</div>
        <h1 className="text-xl md:text-2xl tracking-tight font-medium mt-1 text-[#F1F5F9]">Leaderboard</h1>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 bg-[#15171E] border border-[#2A2D3A] rounded-md px-3 py-2">
          <MagnifyingGlassIcon size={14} className="text-[#64748B]" />
          <input
            data-testid="search-input"
            placeholder="Search bot name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-transparent text-sm outline-none w-56 mono text-[#F1F5F9] placeholder:text-[#64748B]"
          />
        </div>

        <div className="flex gap-1 ml-2" data-testid="status-filter-group">
          {STATUSES.map((s) => (
            <button
              key={s}
              data-testid={`filter-status-${s}`}
              onClick={() => setStatusFilter(s)}
              className={`text-[10px] mono uppercase tracking-widest px-2.5 py-1.5 rounded-sm border transition-colors ${
                statusFilter === s
                  ? "bg-[#D6FF38]/10 text-[#D6FF38] border-[#D6FF38]/30"
                  : "bg-transparent text-[#94A3B8] border-[#2A2D3A] hover:border-[#D6FF38]/40 hover:text-[#F1F5F9]"
              }`}
            >
              {s === "all" ? "All" : STATUS_META[s].label}
            </button>
          ))}
        </div>

        <select
          data-testid="symbol-filter"
          value={symbolFilter}
          onChange={(e) => setSymbolFilter(e.target.value)}
          className="bg-[#15171E] border border-[#2A2D3A] rounded-md px-3 py-1.5 text-sm mono text-[#F1F5F9] outline-none ml-auto"
        >
          {symbols.map((s) => <option key={s} value={s}>{s === "all" ? "All Symbols" : s}</option>)}
        </select>

        <div className="text-[11px] mono text-[#64748B] ml-2">{filtered.length} bots</div>
      </div>

      <div className="border border-[#2A2D3A] bg-[#15171E] rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="leaderboard-table">
          <thead>
            <tr
              className="text-[10px] mono uppercase tracking-widest text-[#D6FF38]/90 border-b border-[#D6FF38]/20"
              style={{
                background:
                  "linear-gradient(180deg, rgba(214,255,56,0.08) 0%, rgba(214,255,56,0.02) 100%)",
              }}
            >
              <SortHeader k="name" {...sortProps}>Bot</SortHeader>
              <SortHeader k="symbol" {...sortProps}>Symbol</SortHeader>
              <th className="text-left py-2 font-normal">Status</th>
              <SortHeader k="starting_balance" align="right" {...sortProps}>Start $</SortHeader>
              <SortHeader k="final_balance" align="right" {...sortProps}>Final $</SortHeader>
              <SortHeader k="growth_pct" align="right" {...sortProps}>Growth %</SortHeader>
              <SortHeader k="average_month" align="right" {...sortProps}>Avg/Mo</SortHeader>
              <SortHeader k="profit_month_pct" align="right" {...sortProps}>Win-Mo %</SortHeader>
              <SortHeader k="best_month" align="right" {...sortProps}>Best Mo</SortHeader>
              <SortHeader k="worst_month" align="right" {...sortProps}>Worst Mo</SortHeader>
              <SortHeader k="balance_dd" align="right" {...sortProps}>Bal DD</SortHeader>
              <SortHeader k="equity_dd" align="right" {...sortProps}>Eq DD</SortHeader>
              <SortHeader k="profit_factor" align="right" {...sortProps}>PF</SortHeader>
              <SortHeader k="sharpe" align="right" {...sortProps}>Sharpe</SortHeader>
              <SortHeader k="total_trades" align="right" {...sortProps}>Trades</SortHeader>
              <th className="text-right py-2 font-normal pr-4">Curve</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((b, i) => {
              const isGy = b.status === "graveyard";
              const equity = (b.monthly_results || []).map((m) => ({ x: m.month, y: m.y ?? m.final_balance }));
              return (
                <tr
                  key={b.id}
                  data-testid={`bot-row-${i}`}
                  className={`border-b border-[#2A2D3A]/40 hover:bg-white/[0.02] transition-colors ${isGy ? "opacity-50" : ""} row-fade-in`}
                  style={{ animationDelay: `${Math.min(i * 15, 600)}ms` }}
                >
                  <td className="py-2.5 pl-4">
                    <Link to={`/bot/${b.id}`} className="mono text-[#F1F5F9] hover:text-[#D6FF38]">
                      <BotName name={b.name} maxLen={36} />
                    </Link>
                    {b.suspicious && <SuspiciousBadge />}
                  </td>
                  <td className="py-2.5 mono text-[#94A3B8]">{b.symbol}</td>
                  <td className="py-2.5"><StatusBadge status={b.status} /></td>
                  <td className="py-2.5 mono text-right text-[#94A3B8]">{fmt.money(b.starting_balance, "$")}</td>
                  <td className="py-2.5 mono text-right">{fmt.money(b.final_balance, "$")}</td>
                  <td className="py-2.5 mono text-right" style={{ color: b.growth_pct >= 0 ? "#2ECA7F" : "#FF4D4D" }}>
                    {fmt.signed(b.growth_pct)}%
                  </td>
                  <td className="py-2.5 mono text-right" style={{ color: b.average_month >= 0 ? "#2ECA7F" : "#FF4D4D" }}>
                    {fmt.signed(b.average_month)}%
                  </td>
                  <td className="py-2.5 mono text-right">{b.profit_month_pct}%</td>
                  <td className="py-2.5 mono text-right text-[#2ECA7F]">{fmt.signed(b.best_month)}%</td>
                  <td className="py-2.5 mono text-right text-[#FF4D4D]">{fmt.signed(b.worst_month)}%</td>
                  <td className="py-2.5 mono text-right text-[#FF4D4D]">{(b.balance_dd ?? b.max_drawdown ?? 0)}%</td>
                  <td className="py-2.5 mono text-right text-[#F97316]">{b.equity_dd ?? 0}%</td>
                  <td className="py-2.5 mono text-right">{b.profit_factor}</td>
                  <td className="py-2.5 mono text-right text-[#94A3B8]">{b.sharpe || "—"}</td>
                  <td className="py-2.5 mono text-right text-[#94A3B8]">{b.total_trades || 0}</td>
                  <td className="py-2.5 pr-4" style={{ width: 120 }}>
                    <div className="h-8 w-[110px] ml-auto">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={equity}>
                          <Line
                            type="monotone"
                            dataKey="y"
                            stroke={b.growth_pct >= 0 ? "#2ECA7F" : "#FF4D4D"}
                            strokeWidth={1.5}
                            dot={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan="15" className="text-center py-8 text-[#64748B] mono text-xs">No bots match these filters</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
